import { Inject } from '@odinjs/odin';

import { FieldAPI, FormAPI, InputAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnBeforeCancel, OnBeforeSubmit, OnChange, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';

import moment from 'moment';

import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { FragmentNames, ReservedBloodProductsTerminateAliases } from '../../../../../codegen';
import { ISO_FULL_DATE_FORMAT } from '../../../../../constants/date-utils';
import { Endpoint } from '../../../../../constants/endpoints';
import { ApplicationRoute } from '../../../../../constants/routes';
import BaseController from '../../../../BaseController';
import MedicationService from '../../../services/medication-service';
import { Value } from '@philips-internal/oswald-ui-core';
import { PrescriptionType, solutionMapping } from '../../../services/timeline-service';
import { AdminModalsService } from '../../../services/admin-modals-service';
import { ReservedProduction } from '../../blood-management/blood-management-types';

@Controller({ component: ReservedBloodProductsTerminateAliases.RESERVED_BLOOD_PRODUCTS_TERMINATE_LAYOUT, fragment: FragmentNames.RESERVED_BLOOD_PRODUCTS_TERMINATE })
export default class ReservedBloodProductsTerminateController extends BaseController {

  @Inject(ReservedBloodProductsTerminateAliases.RESERVED_BLOOD_PRODUCTS_TERMINATE_FORM)
  private terminateFormAPI!: FormAPI;

  @Inject(ReservedBloodProductsTerminateAliases.PARTIAL_VOLUME_INPUT)
  private partialVolumeInputAPI!: InputAPI;

  @Inject(ReservedBloodProductsTerminateAliases.DISCARDED_VOLUME_INPUT)
  private discardedVolumeInputAPI!: InputAPI;

  @Inject(ReservedBloodProductsTerminateAliases.NOTES_TEXT_AREA)
  private notesJustificationTextAreaAPI!: InputAPI;

  @Inject(ReservedBloodProductsTerminateAliases.INFUSED_VOLUME_INPUT)
  private infusedVolumeInputAPI!: InputAPI;

  @Inject(ReservedBloodProductsTerminateAliases.SCHEDULE_DATE_INPUT)
  private scheduleDateInputApi!: InputAPI;

  @Inject(ReservedBloodProductsTerminateAliases.TITLE_FIELD)
  private titleFieldApi!: FieldAPI;

  @OnMount(ReservedBloodProductsTerminateAliases.RESERVED_BLOOD_PRODUCTS_TERMINATE_LAYOUT)
  handleMountReservedBloodProductsTerminateLayout(): void {
    const reservedProduct = this.context.getBulkData() as unknown as ReservedProduction;
    this.context.set({
      reservedProduct: { ...reservedProduct },
      scheduleDate: reservedProduct.reservationDate
    });
    this.setDataPanelTitle(reservedProduct);
  }

  @OnMount(ReservedBloodProductsTerminateAliases.RESERVED_BLOOD_PRODUCTS_TERMINATE_FORM)
  handleMountReservedBloodProductsTerminateForm(event: MountEvent): void {
    const formApi = event.target as FormAPI
    const defaultValue = 0;
    const reservedProduct = this.context.getBulkData() as unknown as ReservedProduction;
    formApi.setData({
      ...reservedProduct,
      discardedVolume: defaultValue,
      infusedVolume: reservedProduct.volume,
      partialVolume: defaultValue,
      stageVolume: reservedProduct.volume
    });
  }

  private setDataPanelTitle(reservedProduct: ReservedProduction): void {
    const scheduleDate = moment(reservedProduct.reservationDate).utc().format(ISO_FULL_DATE_FORMAT);
    this.scheduleDateInputApi.setValue(scheduleDate);
    this.scheduleDateInputApi.setReadOnly(true);
    this.titleFieldApi.setData({ materialDescription: reservedProduct.description });
  }

  @OnMount(ReservedBloodProductsTerminateAliases.DATE_INPUT)
  handleDateFieldMount({ target: inputApi }: EventData<InputAPI>): void {
    const currentTimeMoment = moment();
    this.context.set({ currentTime: currentTimeMoment.format(ISO_FULL_DATE_FORMAT) });
    inputApi.setValue(currentTimeMoment.utc().format(ISO_FULL_DATE_FORMAT));
  }

  @OnBeforeSubmit(ReservedBloodProductsTerminateAliases.RESERVED_BLOOD_PRODUCTS_TERMINATE_FORM)
  handleBeforeSubmit(): void {
    const body = this.getRequestBody()
    this.generatePresciptionChanges(body);
  }

  @OnBeforeCancel(ReservedBloodProductsTerminateAliases.RESERVED_BLOOD_PRODUCTS_TERMINATE_FORM)
  handleCancelForm(): void {
    applicationContext.popNavigationItem();
  }

  @OnChange(ReservedBloodProductsTerminateAliases.INFUSED_VOLUME_INPUT)
  handleInfusedVolumeInputChange(event: ChangeEvent<string>): void {
    this.handleVolumeInputChange(event, this.discardedVolumeInputAPI);
  }

  @OnChange(ReservedBloodProductsTerminateAliases.DISCARDED_VOLUME_INPUT)
  handleDiscardedVolumeInputChange(event: ChangeEvent<string>): void {
    this.handleVolumeInputChange(event, this.infusedVolumeInputAPI);
  }

  private getRequestBody(): Record<string, Value> {
    const reservedProduct = this.context.get('reservedProduct');
    const { currentTime } = this.context.getBulkData() as { currentTime: string };
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const changeTypeTerminate = 4;
    const dropsPerMin = 'gtm'

    return {
      bagNumber: reservedProduct.bagNumber,
      changeDate: currentTime,
      changeType: changeTypeTerminate,
      dateTime: null,
      despisedVolumeQuantity: this.discardedVolumeInputAPI.getValue(),
      dosageType: dropsPerMin,
      encounterNumber: encounterId as number,
      infusedVolumeQuantity: this.infusedVolumeInputAPI.getValue(),
      justificationDescription: this.notesJustificationTextAreaAPI.getValue() as string ?? '',
      partialVolumeQuantity: this.partialVolumeInputAPI.getValue(),
      prescriptionNumber: reservedProduct.prescriptionNumber,
      reservationProductSequenceNumber: reservedProduct.id,
      reservationSequenceNumber: reservedProduct.reservationId,
      solutionSequenceNumber: reservedProduct.materialSequence ?? 0,
      solutionType: solutionMapping[PrescriptionType.BLOOD_MANAGEMENT],
      volumePhaseQuantity: this.infusedVolumeInputAPI.getValue() as number,
    }
  }

  private generatePresciptionChanges(body: Record<string, Value>): void {
    this.terminateFormAPI.setLoading(true)
    this.terminateFormAPI.setEditing(false)
    HttpService.post(Endpoint.ADEP_GENERATE_CHANGE_SOLUCAO_PRESCR, {}, { body })
      .then(response => {
        const responseBody = response.body as { messageDescription: string };
        if (responseBody) {
          this.showResponseMessage(responseBody?.messageDescription);
          this.redirectToTimeline();
        }
      }).catch((error) => {
        if (error.body && error.body.errors) {
          error.body.errors.forEach((message: string) => MedicationService.showToast(MessageService.translate(message), ToastLevel.ERROR));
        }
      }).finally(() => {
        this.terminateFormAPI.setEditing(true)
        this.terminateFormAPI.setLoading(false)
      });
  }
  private redirectToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
    AdminModalsService.redirectToTimeline(FragmentNames.RESERVED_BLOOD_PRODUCTS_TERMINATE);
    AdminModalsService.redirectToTimeline(FragmentNames.RESERVED_BLOOD_PRODUCTS_VIEWER);
  }

  private showResponseMessage(message: string): void {
    if (message) {
      MedicationService.showToast(message, ToastLevel.ERROR);
      return
    }
    MedicationService.showToast(MessageService.translate('txt.item-terminate-with-success'), ToastLevel.SUCCESS);
  }

  private isValidChar(value: string, oldValue?: string): boolean {
    return value === '.' || value === ',' || (value === '' && oldValue !== '')
  }

  private handleVolumeInputChange({ value, oldValue }: ChangeEvent<string>, input: InputAPI): void {
    const actualValue = Number(value) || 0;
    if (this.isValidChar(value, oldValue) || isNaN(actualValue) || oldValue === value) {
      return
    }
    const reservedProduct = this.context.getBulkData() as unknown as ReservedProduction;
    const doseQuantity = Number(reservedProduct.volume);
    const partialVolume = Number(this.partialVolumeInputAPI.getValue());
    const updatedVolume = this.calculateVolumeSolution(actualValue, partialVolume, doseQuantity);
    input.setValue(String(updatedVolume));
  }

  private calculateVolumeSolution(infusedVolume: number, partialVolume: number, stageVolume: number): number {
    const defaultValue = 0;
    if (stageVolume > defaultValue) {
      const result = stageVolume - partialVolume - infusedVolume;
      return Math.max(defaultValue, parseFloat(result.toFixed(2)));
    }

    return defaultValue;
  }
}